import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import HeroSection from "../../components/HeroSection";
import GetStartedFreeTrial from "../../components/GetStartedFreeTrial";
import { getPlans } from "../../services/Plan";
import { Helmet } from "react-helmet-async";

const Pricing = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getPlans()
            .then((data) => {
                const normalized = data?.map((plan: any) => ({
                    ...plan,
                    billingCycle: plan?.billingCycle?.toLowerCase(),
                }));
                setPlans(normalized);
            })
            .catch((error) => console.error("Error fetching plans data:", error))
            .finally(() => setLoading(false));
    }, []);

    const handleCheckout = (plan: any) => {
        navigate("/checkout", {
            state: {
                planId: plan.id,
                planName: plan.planName,
                price: plan.price,
                billingCycle: plan.billingCycle,
            },
        });
    };

    const monthlyPlans = plans?.filter((p) => p?.billingCycle === "month");
    const yearlyPlans = plans?.filter((p) => p?.billingCycle === "year");

    return (
        <div className="w-full flex flex-col items-center">
            <Helmet>
                <title>Vehicle Rental Management Software Pricing | RD Rental</title>
                <meta
                    name="description"
                    content="View RD Rental pricing plans for vehicle rental management software. Flexible monthly and yearly plans with a 30-day free trial. No hidden fees."
                />
            </Helmet>


            <HeroSection
                mainHeading="Vehicle Rental Management Software Pricing"
                backgroundImage="../images/pricing-rd-rental.png"
            />
            <section className="max-w-5xl text-center px-6 py-10">
                <h1 className="text-3xl font-bold text-gray-800 mb-4">
                    Simple & Transparent Pricing for Rental Businesses
                </h1>
                <p className="text-gray-600 max-w-3xl mx-auto">
                    Choose a flexible pricing plan that fits your vehicle rental business.
                    RD Rental offers affordable monthly and yearly plans with full access
                    to fleet management, booking automation, billing, and reporting features.
                </p>
            </section>

            <section className="w-full max-w-6xl mx-auto px-6 py-12">
                <h2 className="text-3xl font-bold text-center mb-10 text-gray-800">
                    Our Plans
                </h2>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <p className="mt-4 text-gray-600 font-medium">Loading...</p>
                    </div>
                ) : plans.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-12 h-12 text-gray-400 mb-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 13h6m2 4H7a2 2 0 01-2-2V7a2 2 0 012-2h6l4 4v6a2 2 0 01-2 2z"
                            />
                        </svg>
                        <h3 className="text-lg font-semibold text-gray-700 mb-1">
                            No Plans Available
                        </h3>
                        <p className="text-gray-500 text-sm">
                            Please check back later — we’re updating our subscription plans.
                        </p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-10">
                        <div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
                                Monthly Plans
                            </h3>
                            {monthlyPlans?.length > 0 ? (
                                <div className="grid gap-6">
                                    {monthlyPlans?.map((plan) => (
                                        <div
                                            key={plan?.id}
                                            className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 p-6 flex flex-col items-center text-center border border-gray-100"
                                        >
                                            <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                                {plan?.planName}
                                            </h3>
                                            <p className="text-gray-600 mb-4 text-sm">
                                                {plan?.description}
                                            </p>
                                            <p className="text-3xl font-bold text-[#167AC3] mb-4">
                                                ${plan?.price}
                                                <span className="text-base font-medium text-gray-500">
                                                    /month
                                                </span>
                                            </p>
                                            <button
                                                onClick={() => handleCheckout(plan)}
                                                className="mt-auto bg-[#167AC3] text-white font-semibold py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                                            >
                                                Start Membership
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-gray-500">No monthly plans available.</p>
                            )}
                        </div>

                        <div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
                                Yearly Plans
                            </h3>
                            {yearlyPlans.length > 0 ? (
                                <div className="grid gap-6">
                                    {yearlyPlans.map((plan) => (
                                        <div
                                            key={plan?.id}
                                            className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 p-6 flex flex-col items-center text-center border border-gray-100"
                                        >
                                            <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                                {plan?.planName}
                                            </h3>
                                            <p className="text-gray-600 mb-4 text-sm">
                                                {plan?.description}
                                            </p>
                                            <p className="text-3xl font-bold text-[#167AC3] mb-4">
                                                ${plan?.price}
                                                <span className="text-base font-medium text-gray-500">
                                                    /year
                                                </span>
                                            </p>
                                            <button
                                                onClick={() => handleCheckout(plan)}
                                                className="mt-auto bg-[#167AC3] text-white font-semibold py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                                            >
                                                Start Membership
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-gray-500">No yearly plans available.</p>
                            )}
                        </div>
                    </div>
                )}
            </section>

            <section className="w-full bg-gray-50 py-16 px-6">
                <div className="max-w-6xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-gray-800 mb-4">
                        Try it FREE for 30 days.
                    </h2>
                    <p className="text-gray-600 max-w-3xl mx-auto mb-12">
                        Experience the power of RD Rental with our risk-free trial offer.
                        Sign up today and gain access to all the features and functionalities
                        of RD Rental for 30 days, absolutely free.
                    </p>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <h3 className="font-semibold text-lg text-gray-800 mb-2">
                                Complete Vehicle Management
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Track and manage your entire fleet — cars, trucks, and cargo
                                vehicles — all from one central dashboard.
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <h3 className="font-semibold text-lg text-gray-800 mb-2">
                                Real-Time Availability & Scheduling
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Instantly check vehicle availability, assign bookings, and avoid
                                scheduling conflicts with automated updates.
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <h3 className="font-semibold text-lg text-gray-800 mb-2">
                                Easy Billing & Maintenance Tracking
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Automate invoices, monitor maintenance schedules, and keep your
                                vehicles in top condition with timely reminders.
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <h3 className="font-semibold text-lg text-gray-800 mb-2">
                                Reliable Customer & Driver Support
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Manage driver assignments, respond to customer inquiries quickly,
                                and ensure smooth rental experiences every time.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <GetStartedFreeTrial />
        </div>
    );
};

export default Pricing;